/**
 * Marketplace formatting + geometry helpers (Macro #29 Stage 3).
 * Pure functions — no fabricated economic state; they only present
 * backend-authoritative micro amounts / cell sets.
 */
import type { Listing, MarketplaceConfig } from "../../lib/marketplace-api";

/** True when a listing is a partial (wrapped subdivision-vault child) LAND sale. */
export function isPartialListing(listing: Pick<Listing, "unit_kind" | "child_token_id">): boolean {
  return listing.unit_kind === "legacy_v1_wrapped_child" || listing.child_token_id != null;
}

/** The parcel id to display for a listing: the PARENT parcel for a partial sale. */
export function displayParcelId(
  listing: Pick<Listing, "unit_kind" | "child_token_id" | "parent_token_id" | "token_id">,
): string {
  if (isPartialListing(listing) && listing.parent_token_id != null) return String(listing.parent_token_id);
  return String(listing.token_id);
}

/** Format a micro (1e-6) integer amount to a human string, e.g. 480000000 → "480.00". */
export function fmtMicro(micro: number | undefined | null, decimals = 6): string {
  if (micro == null || Number.isNaN(micro)) return "—";
  const v = Number(micro) / 10 ** decimals;
  const fixed = v >= 1000 ? v.toLocaleString("en-US", { maximumFractionDigits: 0 }) : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return fixed;
}

/** Resolve a currency address → ticker symbol using authoritative config. */
export function symbolFor(address: string | null | undefined, config?: MarketplaceConfig | null): string {
  if (!address) return "USDC";
  const a = address.toLowerCase();
  const match = config?.currencies.find((c) => c.address && c.address.toLowerCase() === a);
  return match?.symbol ?? "USDC";
}

export interface BBox { x: number; y: number; w: number; h: number; }

/** Bounding rectangle of a listed cell set. */
export function bboxFromCells(cells: [number, number][]): BBox {
  if (!cells || cells.length === 0) return { x: 0, y: 0, w: 1, h: 1 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of cells) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

export function slotCount(cells: [number, number][]): number {
  return cells?.length ?? 0;
}

export function pricePerSlotMicro(priceMicro: number, cells: [number, number][]): number {
  const n = slotCount(cells);
  return n > 0 ? Math.round(priceMicro / n) : priceMicro;
}

export function shortAddr(a: string | null | undefined): string {
  if (!a) return "—";
  const s = String(a);
  return s.length > 12 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
}

/** Relative time until a real (seconds) timestamp, e.g. "in 29d", "expired". */
export function fmtExpiry(realTs: number): string {
  const now = Math.floor(Date.now() / 1000);
  const d = realTs - now;
  if (d <= 0) return "expired";
  if (d < 3600) return `in ${Math.max(1, Math.round(d / 60))}m`;
  if (d < 86400) return `in ${Math.round(d / 3600)}h`;
  return `in ${Math.round(d / 86400)}d`;
}

/** Countdown from a delta in seconds → "2d 03h", "4h 12m", "08m 30s", "Ended". */
export function fmtCountdown(deltaSeconds: number): string {
  const d = Math.floor(deltaSeconds);
  if (d <= 0) return "Ended";
  const days = Math.floor(d / 86400);
  const hours = Math.floor((d % 86400) / 3600);
  const mins = Math.floor((d % 3600) / 60);
  const secs = d % 60;
  if (days > 0) return `${days}d ${String(hours).padStart(2, "0")}h`;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, "0")}m`;
  return `${String(mins).padStart(2, "0")}m ${String(secs).padStart(2, "0")}s`;
}

/** Canonical zone key from a backend quote `zone` string. */
export type ZoneKey = "standard" | "premium" | "vip" | "reserve" | "grid_zero" | "unknown";

export function zoneKey(raw: string | null | undefined): ZoneKey {
  const s = String(raw ?? "").toLowerCase();
  if (s.includes("grid") && s.includes("zero")) return "grid_zero";
  if (s.includes("vip")) return "vip";
  if (s.includes("premium")) return "premium";
  if (s.includes("reserve")) return "reserve";
  if (s.includes("standard")) return "standard";
  return "unknown";
}

export const ZONE_LABEL: Record<ZoneKey, string> = {
  standard: "Standard",
  premium: "Premium",
  vip: "VIP",
  reserve: "Reserve",
  grid_zero: "Grid Zero",
  unknown: "—",
};
